import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, RefObject, SetStateAction } from 'react';
import { chatRepository } from '../../../db/repository';
import type { ImClient } from '../../../im/imClient';
import { ImManager } from '../../../im/imManager';
import type { ChatMessage, ChatSession, SessionKind, SessionMap } from '@domain/chat/types';
import {
  applyAckToSession,
  applyOptimisticMessageToSession,
  createOptimisticTextMessage,
  hydrateSessionMessages,
} from '@domain/chat/outgoingMessageState.js';
import type { ConnState, LogEntry } from '@shared/config/runtime';
import {
  AUDIO_PREVIEW_TEXT,
  AUDIO_RECORDING_MIN_MS,
  buildAudioAssetMessageData,
  buildAudioMessageData,
  FLUTTER_COMPAT_AUDIO_MIME,
  resolveAudioMsgType,
} from '@shared/utils/audio';
import { startPcmAudioRecording } from '@shared/utils/audioRecording';
import { transcodeRecordedAudioToFlutterOpus } from '@shared/utils/audioTranscode';
import { fallbackImageMeta, getErrorMessage, nextLocalMessageId, readBrowserImageMeta, wrapImagePayload, wrapLocalImagePayload } from '@shared/utils/ui';
import { uploadMedia, type MediaAsset } from '@domain/media/api';
import { processImage } from '@shared/media/processing/imageProcessor';
import type { EmojiComposerHandle } from '../../../emoji/EmojiComposer';

type UseWorkspaceMessagingOptions = {
  connState: ConnState;
  composerText: string;
  setComposerText: Dispatch<SetStateAction<string>>;
  pushLog: (msg: string, kind?: LogEntry['kind']) => void;
  imRef: MutableRefObject<ImClient | null>;
  imUserIdRef: MutableRefObject<number | null>;
  sessionsRef: MutableRefObject<SessionMap>;
  activeSessionKeyRef: MutableRefObject<string | null>;
  setSessionsByKey: Dispatch<SetStateAction<SessionMap>>;
  composerRef: RefObject<EmojiComposerHandle | null>;
  sessionTypeToProtoValue: (sessionType: SessionKind) => number;
  dedupeMessages: (messages: ChatMessage[]) => ChatMessage[];
  uploadImage: (file: File, apiBase: string, session: ChatSession, metadata?: Record<string, unknown>) => Promise<MediaAsset>;
  apiBase: string;
};

function ensureSuccessfulAck(msgId: number): number {
  if (msgId <= 0) {
    throw new Error('Message was blocked or rejected by server');
  }
  return msgId;
}

export function useWorkspaceMessaging({
  connState,
  composerText,
  setComposerText,
  pushLog,
  imRef,
  imUserIdRef,
  sessionsRef,
  activeSessionKeyRef,
  setSessionsByKey,
  composerRef,
  sessionTypeToProtoValue,
  dedupeMessages,
  uploadImage,
  apiBase,
}: UseWorkspaceMessagingOptions) {
  const [isAudioMode, setIsAudioMode] = useState(false);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [recordingLabel, setRecordingLabel] = useState('按住说话');
  const [recordingLevels, setRecordingLevels] = useState<number[]>([0.18, 0.26, 0.34, 0.26, 0.18]);
  const audioRecordingRef = useRef<Awaited<ReturnType<typeof startPcmAudioRecording>> | null>(null);

  useEffect(() => () => {
    if (audioRecordingRef.current) {
      void audioRecordingRef.current.cancel();
      audioRecordingRef.current = null;
    }
  }, []);

  const loadHistory = useCallback(async (
    sessionKey: string,
    mode: 'initial' | 'older' | 'newer' = 'initial',
  ) => {
    if (!imRef.current || imUserIdRef.current == null) return;
    const session = sessionsRef.current[sessionKey];
    if (!session) return;

    if (mode === 'older') {
      const oldestMsg = session.messages[0];
      const beginMsgId = oldestMsg ? Math.max(oldestMsg.msgId - 1, 0) : 0;
      if (beginMsgId <= 1) {
        setSessionsByKey((prev) => {
          const current = prev[sessionKey];
          if (!current) return prev;
          return {
            ...prev,
            [sessionKey]: {
              ...current,
              historyState: 'ready',
              historyError: null,
              hasMoreHistory: false,
            },
          };
        });
        return;
      }
    }

    setSessionsByKey((prev) => {
      const current = prev[sessionKey];
      if (!current) return prev;
      return {
        ...prev,
        [sessionKey]: { ...current, historyState: 'loading' },
      };
    });

    let displayedLocalInitial = false;

    try {
      if (mode === 'older') {
        const oldestMsg = session.messages[0];
        const localList = oldestMsg
          ? await chatRepository.getMessagesBySession(sessionKey, 20, oldestMsg.ts)
          : [];

        if (localList.length > 0) {
          const mergedMessages = dedupeMessages([...localList, ...session.messages]);
          const earliestMsgId = mergedMessages.length > 0 ? mergedMessages[0].msgId : 0;
          setSessionsByKey((prev) => {
            const current = prev[sessionKey];
            if (!current) return prev;
            return {
              ...prev,
              [sessionKey]: {
                ...current,
                messages: mergedMessages,
                historyState: 'ready',
                historyError: null,
                hasMoreHistory: earliestMsgId > 1,
              },
            };
          });
          return;
        }
      }

      if (mode === 'initial') {
        const localLimit = Math.max(session.messages.length + 20, 20);
        const localFresh = await chatRepository.getMessagesBySession(sessionKey, localLimit);

        if (localFresh.length > 0) {
          const localNewestMsgId = localFresh[0].msgId;
          const earliestMsgId = localFresh[localFresh.length - 1].msgId;
          const localMessages = dedupeMessages(localFresh.slice().reverse());

          setSessionsByKey((prev) => {
            const current = prev[sessionKey];
            if (!current) return prev;
            return {
              ...prev,
              [sessionKey]: {
                ...current,
                messages: localMessages,
                unreadCount: activeSessionKeyRef.current === sessionKey ? 0 : current.unreadCount,
                historyState: 'ready',
                historyError: null,
                hasMoreHistory: earliestMsgId > 1,
              },
            };
          });

          displayedLocalInitial = true;
          ImManager.getInstance().ackMsgRead(localFresh[0]);

          if (session.lastMsgId > localNewestMsgId) {
            ImManager.getInstance().fetchMissingMsgs(
              sessionKey,
              session.sessionId,
              session.sessionType,
              session.lastMsgId,
              20,
            );
          }
        }

        const response = await imRef.current.requestMessageHistory(
          imUserIdRef.current,
          session.sessionId,
          sessionTypeToProtoValue(session.sessionType),
          0,
          Math.max(session.messages.length + 20, 20),
        );

        const mapped = response.getMsgListList().map((msgInfo: any) => {
          const msgType = msgInfo.getMsgType();
          const fromId = msgInfo.getFromSessionId?.() || msgInfo.getFromUserId?.();
          const bytes = msgInfo.getMsgData_asU8?.() ?? new Uint8Array();
          const text = ImManager.getInstance().decodeMsgData(bytes, msgType);

          return {
            localId: `history_${sessionKey}_${msgInfo.getMsgId()}`,
            msgId: msgInfo.getMsgId(),
            sessionKey,
            sessionId: session.sessionId,
            sessionType: session.sessionType,
            from: fromId,
            text,
            msgType,
            msgData: bytes,
            ts: msgInfo.getCreateTime() * 1000,
            dir: fromId === imUserIdRef.current ? 'out' as const : 'in' as const,
            status: 'sent' as const,
          };
        });

        if (mapped.length > 0) {
          await chatRepository.saveMessages(mapped);
        }
      }

      if (mode === 'older') {
        const oldestMsg = session.messages[0];
        const beginMsgId = oldestMsg ? Math.max(oldestMsg.msgId - 1, 0) : 0;
        if (beginMsgId > 1) {
          const response = await imRef.current.requestMessageHistory(
            imUserIdRef.current,
            session.sessionId,
            sessionTypeToProtoValue(session.sessionType),
            beginMsgId,
            20,
          );

          const mapped = response.getMsgListList().map((msgInfo: any) => {
            const msgType = msgInfo.getMsgType();
            const fromId = msgInfo.getFromSessionId?.() || msgInfo.getFromUserId?.();
            const bytes = msgInfo.getMsgData_asU8?.() ?? new Uint8Array();
            const text = ImManager.getInstance().decodeMsgData(bytes, msgType);

            return {
              localId: `history_${sessionKey}_${msgInfo.getMsgId()}`,
              msgId: msgInfo.getMsgId(),
              sessionKey,
              sessionId: session.sessionId,
              sessionType: session.sessionType,
              from: fromId,
              text,
              msgType,
              msgData: bytes,
              ts: msgInfo.getCreateTime() * 1000,
              dir: fromId === imUserIdRef.current ? 'out' as const : 'in' as const,
              status: 'sent' as const,
            };
          });

          if (mapped.length > 0) {
            await chatRepository.saveMessages(mapped);
          } else {
            setSessionsByKey((prev) => {
              const current = prev[sessionKey];
              if (!current) return prev;
              return {
                ...prev,
                [sessionKey]: {
                  ...current,
                  historyState: 'ready',
                  historyError: null,
                  hasMoreHistory: false,
                },
              };
            });
            return;
          }
        }
      }

      const fresh = await chatRepository.getMessagesBySession(sessionKey, session.messages.length + 20);
      const localNewestMsgId = fresh.length > 0 ? fresh[0].msgId : 0;
      const earliestMsgId = fresh.length > 0 ? fresh[fresh.length - 1].msgId : 0;

      if (mode === 'initial' && session.lastMsgId > localNewestMsgId) {
        ImManager.getInstance().fetchMissingMsgs(
          sessionKey,
          session.sessionId,
          session.sessionType,
          session.lastMsgId,
          20,
        );
      }

      if (mode === 'initial' && fresh.length > 0) {
        ImManager.getInstance().ackMsgRead(fresh[0]);
      }

      setSessionsByKey((prev) => {
        const current = prev[sessionKey];
        if (!current) return prev;
        return {
          ...prev,
          [sessionKey]: {
            ...current,
            messages: dedupeMessages(fresh.slice().reverse()),
            unreadCount: activeSessionKeyRef.current === sessionKey ? 0 : current.unreadCount,
            historyState: 'ready',
            historyError: null,
            hasMoreHistory: earliestMsgId > 1,
          },
        };
      });
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      pushLog(
        displayedLocalInitial
          ? `同步最新历史记录失败，已保留本地记录: ${errorMsg}`
          : `加载历史纪录失败: ${errorMsg}`,
        displayedLocalInitial ? 'warn' : 'error',
      );
      setSessionsByKey((prev) => {
        const current = prev[sessionKey];
        if (!current) return prev;
        return {
          ...prev,
          [sessionKey]: displayedLocalInitial
            ? { ...current, historyState: 'ready', historyError: null }
            : { ...current, historyState: 'error', historyError: errorMsg },
        };
      });
    }
  }, [activeSessionKeyRef, dedupeMessages, imRef, imUserIdRef, pushLog, sessionTypeToProtoValue, sessionsRef, setSessionsByKey]);

  const handleSendMessage = useCallback(async (overrideText?: string) => {
    const client = imRef.current;
    const userId = imUserIdRef.current;
    const session = activeSessionKeyRef.current
      ? sessionsRef.current[activeSessionKeyRef.current] ?? null
      : null;
    const text = (overrideText ?? composerText).trim();

    if (!client || connState !== 'online' || userId == null) {
      pushLog('IM 未在线，无法发送消息', 'warn');
      return;
    }
    if (!session) {
      pushLog('请先选择一个会话', 'warn');
      return;
    }
    if (!text) {
      return;
    }

    const optimisticMessage = createOptimisticTextMessage({
      session,
      text,
      userId,
      nextLocalMessageId,
      now: Date.now,
    }) as ChatMessage;

    try {
      await chatRepository.saveMessage(optimisticMessage);
      await chatRepository.saveSession({
        ...session,
        updatedAt: optimisticMessage.ts,
        lastMsgPreview: text,
        lastMsgFromUserId: userId,
      });

      setSessionsByKey((prev) => {
        const current = prev[session.sessionKey] ?? session;
        return {
          ...prev,
          [session.sessionKey]: applyOptimisticMessageToSession(current, optimisticMessage, userId) as ChatSession,
        };
      });

      const ack = await client.sendTextMessage(
        userId,
        session.sessionId,
        sessionTypeToProtoValue(session.sessionType),
        text,
      );
      const serverMsgId = ensureSuccessfulAck(ack.getMsgId?.() ?? 0);
      const serverTimestamp = Date.now();

      if (serverMsgId > 0) {
        await chatRepository.updateMessageWithServerId(optimisticMessage.localId, serverMsgId);
        await chatRepository.saveSession({
          ...session,
          updatedAt: serverTimestamp,
          lastMsgId: serverMsgId,
          lastMsgPreview: text,
          lastMsgFromUserId: userId,
        });

        const fresh = await chatRepository.getMessagesBySession(
          session.sessionKey,
          Math.max(session.messages.length + 20, 20),
        );

        setSessionsByKey((prev) => {
          const current = prev[session.sessionKey];
          if (!current) return prev;

          const acked = applyAckToSession(current, {
            localId: optimisticMessage.localId,
            serverMsgId,
            serverTimestamp,
          });

          return {
            ...prev,
            [session.sessionKey]: hydrateSessionMessages(acked, fresh) as ChatSession,
          };
        });
      }

      if (overrideText == null) {
        setComposerText('');
      }
      pushLog(`→ ${session.title}: ${text}`, 'out');
      composerRef.current?.focus();
    } catch (error) {
      await chatRepository.updateMessageStatus(optimisticMessage.localId, 'failed', 0);

      setSessionsByKey((prev) => {
        const current = prev[session.sessionKey] ?? session;
        const messages = current.messages.some((message) => message.localId === optimisticMessage.localId)
          ? current.messages.map((message) => (
            message.localId === optimisticMessage.localId
              ? { ...message, status: 'failed' as const, msgId: 0 }
              : message
          ))
          : [...current.messages, { ...optimisticMessage, status: 'failed' as const, msgId: 0 }];
        return {
          ...prev,
          [session.sessionKey]: {
            ...current,
            messages: dedupeMessages(messages),
          },
        };
      });

      pushLog(`发送失败: ${getErrorMessage(error)}`, 'error');
    }
  }, [activeSessionKeyRef, composerRef, composerText, connState, dedupeMessages, imRef, imUserIdRef, pushLog, sessionTypeToProtoValue, sessionsRef, setComposerText, setSessionsByKey]);

  const handleSendImage = useCallback(async (file: File) => {
    const client = imRef.current;
    const userId = imUserIdRef.current;
    const session = activeSessionKeyRef.current
      ? sessionsRef.current[activeSessionKeyRef.current] ?? null
      : null;

    if (!client || connState !== 'online' || userId == null) {
      pushLog('IM 未在线，无法发送图片', 'warn');
      return;
    }
    if (!session) {
      pushLog('请先选择一段咨询', 'warn');
      return;
    }

    let processedImage: Awaited<ReturnType<typeof processImage>>;
    try {
      processedImage = await processImage(file, { use: 'chat', quality: 'standard' });
    } catch (error) {
      pushLog(`图片处理失败，未上传原文件: ${getErrorMessage(error)}`, 'error');
      return;
    }
    const processedFile = processedImage.file;
    const imageMeta = {
      ...(await readBrowserImageMeta(processedFile).catch(() => fallbackImageMeta(processedFile))),
      mimeType: processedImage.metadata.mimeType,
      fileName: processedFile.name,
      size: processedFile.size,
      width: processedImage.metadata.width,
      height: processedImage.metadata.height,
    };
    const localUrl = URL.createObjectURL(processedFile);
    const localWrapped = wrapLocalImagePayload(localUrl, imageMeta);
    const localId = nextLocalMessageId();
    const timestamp = Date.now();
    const message: ChatMessage = {
      localId,
      msgId: 0,
      sessionKey: session.sessionKey,
      sessionId: session.sessionId,
      sessionType: session.sessionType,
      from: userId,
      text: localWrapped,
      ts: timestamp,
      dir: 'out',
      status: 'pending',
    };

    setSessionsByKey((prev) => {
      const current = prev[session.sessionKey] ?? session;
      return {
        ...prev,
        [session.sessionKey]: {
          ...current,
          updatedAt: timestamp,
          lastMsgId: current.lastMsgId,
          lastMsgPreview: '[图片]',
          lastMsgFromUserId: userId,
          messages: dedupeMessages([...current.messages, message]),
          historyState: current.historyState === 'idle' ? 'ready' : current.historyState,
          historyError: null,
        },
      };
    });

    let shouldRevoke = false;
    try {
      const media = await uploadImage(processedFile, apiBase, session, {
        ...processedImage.metadata,
        policyVersion: processedImage.policyVersion,
        quality: 'standard',
        sourceSizeBytes: processedImage.source.sizeBytes,
        outputSizeBytes: processedImage.metadata.sizeBytes,
      });
      const remoteWrapped = wrapImagePayload({
        mediaId: media.id,
        provider: 's3',
        url: media.url,
        meta: imageMeta,
      });
      const ack = await client.sendTextMessage(
        userId,
        session.sessionId,
        sessionTypeToProtoValue(session.sessionType),
        remoteWrapped,
      );
      const msgId = ensureSuccessfulAck(ack.getMsgId?.() ?? 0);
      const ackTimestamp = Date.now();

      setSessionsByKey((prev) => {
        const current = prev[session.sessionKey];
        if (!current) return prev;
        const nextMessages: ChatMessage[] = current.messages.map((item) => (
          item.localId === localId
            ? {
              ...item,
              msgId,
              text: remoteWrapped,
              status: 'sent' as const,
              ts: ackTimestamp,
            }
            : item
        ));
        return {
          ...prev,
          [session.sessionKey]: {
            ...current,
            updatedAt: ackTimestamp,
            lastMsgId: msgId || current.lastMsgId,
            lastMsgPreview: '[图片]',
            lastMsgFromUserId: userId,
            messages: dedupeMessages(nextMessages),
          },
        };
      });

      shouldRevoke = true;
      pushLog(`→ ${session.title}: [图片]`, 'out');
    } catch (error) {
      setSessionsByKey((prev) => {
        const current = prev[session.sessionKey];
        if (!current) return prev;
        const nextMessages: ChatMessage[] = current.messages.map((item) => (
          item.localId === localId
            ? {
              ...item,
              status: 'failed' as const,
            }
            : item
        ));
        return {
          ...prev,
          [session.sessionKey]: {
            ...current,
            messages: nextMessages,
          },
        };
      });
      pushLog(`图片发送失败: ${getErrorMessage(error)}`, 'error');
    } finally {
      if (shouldRevoke) {
        URL.revokeObjectURL(localUrl);
      }
    }
  }, [activeSessionKeyRef, apiBase, connState, dedupeMessages, imRef, imUserIdRef, pushLog, sessionTypeToProtoValue, sessionsRef, setSessionsByKey, uploadImage]);

  const handleChatImageSelect = useCallback((files: File[]) => {
    const file = files[0];
    if (file) {
      void handleSendImage(file);
    }
  }, [handleSendImage]);

  const handleSendAudio = useCallback(async (audioBytes: Uint8Array, durationMs: number) => {
    const client = imRef.current;
    const userId = imUserIdRef.current;
    const session = activeSessionKeyRef.current
      ? sessionsRef.current[activeSessionKeyRef.current] ?? null
      : null;

    if (!client || connState !== 'online' || userId == null) {
      pushLog('IM 未在线，无法发送语音', 'warn');
      return;
    }
    if (!session) {
      pushLog('请先选择一段咨询', 'warn');
      return;
    }

    const msgData = buildAudioMessageData(audioBytes, durationMs);
    const localId = nextLocalMessageId();
    const timestamp = Date.now();
    const msgType = resolveAudioMsgType(session.sessionType);
    const message: ChatMessage = {
      localId,
      msgId: 0,
      sessionKey: session.sessionKey,
      sessionId: session.sessionId,
      sessionType: session.sessionType,
      from: userId,
      text: AUDIO_PREVIEW_TEXT,
      msgType,
      msgData,
      audioMimeType: FLUTTER_COMPAT_AUDIO_MIME,
      ts: timestamp,
      dir: 'out',
      status: 'pending',
    };

    pushLog(`语音消息准备入库: ${localId}`, 'info');
    await chatRepository.saveMessage(message);
    await chatRepository.saveSession({
      ...session,
      updatedAt: timestamp,
      lastMsgPreview: AUDIO_PREVIEW_TEXT,
      lastMsgFromUserId: userId,
    });

    setSessionsByKey((prev) => {
      const current = prev[session.sessionKey] ?? session;
      return {
        ...prev,
        [session.sessionKey]: {
          ...current,
          updatedAt: timestamp,
          lastMsgPreview: AUDIO_PREVIEW_TEXT,
          lastMsgFromUserId: userId,
          messages: dedupeMessages([...current.messages, message]),
          historyState: current.historyState === 'idle' ? 'ready' : current.historyState,
          historyError: null,
        },
      };
    });
    pushLog(`语音消息已加入当前会话: ${localId}`, 'info');

    try {
      const audioFile = new File(
        [audioBytes],
        `voice-${Date.now()}.opus`,
        { type: FLUTTER_COMPAT_AUDIO_MIME },
      );
      const media = await uploadMedia(apiBase, audioFile, {
        domain: 'chat',
        kind: 'audio',
        visibility: 'chat_scope',
        scopeType: session.sessionType,
        scopeId: session.sessionId,
        metadata: {
          durationMs,
          mimeType: FLUTTER_COMPAT_AUDIO_MIME,
          size: audioBytes.byteLength,
        },
      });
      const remoteMsgData = buildAudioAssetMessageData({
        kind: 'audio',
        version: 1,
        assetId: media.id,
        provider: 's3',
        url: media.url,
        meta: {
          durationMs,
          mimeType: FLUTTER_COMPAT_AUDIO_MIME,
          fileName: audioFile.name,
          size: audioBytes.byteLength,
        },
      });

      const ack = await client.sendAudioMessage(
        userId,
        session.sessionId,
        sessionTypeToProtoValue(session.sessionType),
        remoteMsgData,
      );
      const msgId = ensureSuccessfulAck(ack.getMsgId?.() ?? 0);
      const ackTimestamp = Date.now();

      if (msgId > 0) {
        await chatRepository.updateMessageWithServerId(localId, msgId);
      }

      setSessionsByKey((prev) => {
        const current = prev[session.sessionKey];
        if (!current) return prev;

        const nextMessages: ChatMessage[] = current.messages.map((item) => (
          item.localId === localId
            ? {
              ...item,
              msgId,
              status: 'sent' as const,
              ts: ackTimestamp,
            }
            : item
        ));

        return {
          ...prev,
          [session.sessionKey]: {
            ...current,
            updatedAt: ackTimestamp,
            lastMsgId: msgId || current.lastMsgId,
            lastMsgPreview: AUDIO_PREVIEW_TEXT,
            lastMsgFromUserId: userId,
            messages: dedupeMessages(nextMessages),
          },
        };
      });

      pushLog(`→ ${session.title}: ${AUDIO_PREVIEW_TEXT}`, 'out');
    } catch (error) {
      setSessionsByKey((prev) => {
        const current = prev[session.sessionKey];
        if (!current) return prev;
        const nextMessages: ChatMessage[] = current.messages.map((item) => (
          item.localId === localId
            ? { ...item, status: 'failed' as const }
            : item
        ));
        return {
          ...prev,
          [session.sessionKey]: {
            ...current,
            messages: nextMessages,
          },
        };
      });
      pushLog(`语音发送失败: ${getErrorMessage(error)}`, 'error');
    }
  }, [activeSessionKeyRef, apiBase, connState, dedupeMessages, imRef, imUserIdRef, pushLog, sessionTypeToProtoValue, sessionsRef, setSessionsByKey]);

  const handleStartAudioRecording = useCallback(async () => {
    console.info('[voice] handleStartAudioRecording');
    const session = activeSessionKeyRef.current
      ? sessionsRef.current[activeSessionKeyRef.current] ?? null
      : null;
    if (!session) {
      pushLog('请先选择一段咨询', 'warn');
      return;
    }
    if (connState !== 'online') {
      pushLog('IM 未在线，无法录制语音', 'warn');
      return;
    }
    if (isRecordingAudio) {
      return;
    }

    try {
      audioRecordingRef.current = await startPcmAudioRecording((level) => {
        setRecordingLevels([
          Math.max(0.16, level * 0.48),
          Math.max(0.2, level * 0.72),
          Math.max(0.24, level),
          Math.max(0.2, level * 0.72),
          Math.max(0.16, level * 0.48),
        ]);
      });
      console.info('[voice] recorder-started');
      setIsRecordingAudio(true);
      setRecordingLabel('松开发送语音');
      pushLog('开始录制语音', 'info');
    } catch (error) {
      console.error('[voice] recorder-start-failed', error);
      pushLog(`无法开始录音: ${getErrorMessage(error)}`, 'error');
    }
  }, [activeSessionKeyRef, connState, isRecordingAudio, pushLog, sessionsRef]);

  const handleStopAudioRecording = useCallback(async (cancelled: boolean) => {
    console.info('[voice] handleStopAudioRecording', { cancelled });
    const recorder = audioRecordingRef.current;
    if (!recorder) {
      console.warn('[voice] handleStopAudioRecording without recorder');
      return;
    }
    audioRecordingRef.current = null;
    setIsRecordingAudio(false);
    setRecordingLevels([0.18, 0.26, 0.34, 0.26, 0.18]);
    setRecordingLabel('按住说话');

    if (cancelled) {
      await recorder.cancel();
      pushLog('已取消语音录制', 'warn');
      return;
    }

    try {
      pushLog('结束录音，正在整理音频数据', 'info');
      const { blob, durationMs } = await recorder.stop();
      console.info('[voice] recorder-stopped', { durationMs, blobType: blob.type, blobSize: blob.size });
      if (durationMs < AUDIO_RECORDING_MIN_MS) {
        pushLog('录音时间太短，请至少按住 0.5 秒', 'warn');
        return;
      }

      setRecordingLabel('正在转换语音...');
      pushLog(`录音完成，时长约 ${Math.round(durationMs / 100) / 10} 秒，开始转码`, 'info');
      console.info('[voice] transcode-start', { durationMs, blobType: blob.type, blobSize: blob.size });
      const { bytes } = await transcodeRecordedAudioToFlutterOpus(blob, blob.type || 'audio/wav');
      if (bytes.length === 0) {
        pushLog('录音内容为空，未发送', 'warn');
        return;
      }

      pushLog(`转码完成，准备写入本地消息并发送，大小 ${bytes.length} 字节`, 'info');
      console.info('[voice] transcode-finished', { bytes: bytes.length });
      await handleSendAudio(bytes, durationMs);
    } catch (error) {
      console.error('[voice] handleStopAudioRecording failed', error);
      pushLog(`语音处理失败: ${getErrorMessage(error)}`, 'error');
    } finally {
      setRecordingLabel('按住说话');
    }
  }, [handleSendAudio, pushLog]);

  return {
    isAudioMode,
    isRecordingAudio,
    recordingLabel,
    recordingLevels,
    setIsAudioMode,
    loadHistory,
    handleSendMessage,
    handleSendImage,
    handleChatImageSelect,
    handleStartAudioRecording,
    handleStopAudioRecording,
  };
}
